import React, {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {ButtonWithIcon, Icon} from '@/components/atoms';
import {FileUploadWithModal} from '@/components/organisms';
import {ButtonIcon} from '@pepsico-ds/ui';
import {Constants} from '@/utils/constants/commonConstants';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {uploadTemplateButtonSchema} from '@/containers/PerfectStore/PerfectStoreSetUp/schemas';

const TargetTemplateUpload = ({
  formData,
  setFormData,
  templateCsv,
  downloadFileNamePrefix,
  cardTitle = 'Store Target',
  uploadModalTitle = 'Upload Target Template',
  uploadModalSubTitle = '',
  uploadFor = Constants.SalesTarget,
  isCsvValidationRequired = false,
  cardClassName = 'border-height mb-2 rounded-lg border-2 border-solid',
  uploadButtonSchema = uploadTemplateButtonSchema,
  uploadButtonLabel,
  customRules,
  onTemplateUpdate,
  salesTargetId
}) => {
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isTargetMediaFileExist = useMemo(
    () =>
      formData?.targetMediaFile?.length > 0 &&
      formData?.targetMediaFile[0] !== '',
    [formData?.targetMediaFile]
  );

  const finalButtonSchema = useMemo(
    () => ({
      ...uploadButtonSchema,
      title: uploadButtonLabel || 'Upload Target Template'
    }),
    [uploadButtonSchema, uploadButtonLabel]
  );

  const handleFileDownload = useCallback(() => {
    const fileName = `${downloadFileNamePrefix}_${getDateWithTimeString()}`;
    downloadCSVFiles(templateCsv, fileName);
  }, [downloadFileNamePrefix, templateCsv]);

  const handleReopenUpload = useCallback(() => {
    setOpenUpload(true);
  }, []);

  const handleSetFormData = useCallback(
    (updater) => {
      setFormData((prev) => {
        const next =
          typeof updater === 'function' ? updater(prev) : {...prev, ...updater};
        if (Object.prototype.hasOwnProperty.call(next, 'storeList')) {
          return {...next, storeTargetFileUrl: next.storeList || ''};
        }
        if (!next.targetMediaFile?.length) {
          return {...next, storeTargetFileUrl: '', storeList: ''};
        }
        return next;
      });
    },
    [setFormData]
  );

  const handleDeleteFile = useCallback(() => {
    setOpenUpload(true);
    handleSetFormData((prev) => ({
      ...prev,
      targetMediaFile: [],
      storeList: ''
    }));
  }, [handleSetFormData]);

  return (
    <>
      <div className={cardClassName}>
        <p className='pb-4 pl-3 pt-2 text-lg font-bold'>{cardTitle}</p>
        <div className='butt-icon mx-3 mb-4 flex items-center'>
          <ButtonIcon
            icon='file_download'
            type='default'
            onClick={handleFileDownload}
            size='small'
            data-testid='download-template-btn'
          />
          {isTargetMediaFileExist ? (
            <div className='target-template-file-chip ml-2 flex !w-full min-w-0 flex-1 items-center rounded-lg px-2 py-1 text-xs'>
              <button
                type='button'
                className='flex min-w-0 flex-1 items-center'
                onClick={handleReopenUpload}
                data-testid='reopen-upload-modal'>
                <Icon
                  alt='file'
                  className='insert_file_icon'
                  icon='insert_drive_file'
                  size='small'
                />
                <span className='min-w-0 flex-1 truncate pl-2 text-left'>
                  {formData?.targetMediaFile?.[0]}
                </span>
              </button>
              <Icon
                alt='delete'
                className='delete_file_icon'
                icon='delete'
                size='small'
                data-testid='file-delete-icon'
                onClick={handleDeleteFile}
              />
            </div>
          ) : (
            <div className='ml-2 min-w-0 flex-1'>
              <ButtonWithIcon
                uiSchema={finalButtonSchema}
                iconLeading='upload'
                handleButton={() => setOpenUpload(true)}
              />
            </div>
          )}
        </div>
      </div>
      <FileUploadWithModal
        openUpload={openUpload}
        setOpenUpload={setOpenUpload}
        setFormData={handleSetFormData}
        formData={formData}
        uploadFor={uploadFor}
        fileType='csv'
        isCsvValidationRequired={isCsvValidationRequired}
        title={uploadModalTitle}
        subTitle={uploadModalSubTitle}
        customRules={customRules}
        required={true}
        uploading={uploading}
        setUploading={setUploading}
        emptyPrimaryButtonText='Upload'
        showDownloadTemplate={true}
        handleDownloadTemplate={handleFileDownload}
        onTemplateUpdate={onTemplateUpdate}
        salesTargetId={salesTargetId}
      />
    </>
  );
};

TargetTemplateUpload.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
  templateCsv: PropTypes.string.isRequired,
  downloadFileNamePrefix: PropTypes.string.isRequired,
  cardTitle: PropTypes.string,
  uploadModalTitle: PropTypes.string,
  uploadModalSubTitle: PropTypes.string,
  uploadFor: PropTypes.string,
  isCsvValidationRequired: PropTypes.bool,
  cardClassName: PropTypes.string,
  uploadButtonSchema: PropTypes.object,
  uploadButtonLabel: PropTypes.string,
  customRules: PropTypes.object,
  onTemplateUpdate: PropTypes.func,
  salesTargetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default TargetTemplateUpload;
